export const APP_NAME = 'camper-diary';
export const API_PREFIX = '/api/v1';
// -----------------------------------------------------------------------------
// Stage — DTO
// -----------------------------------------------------------------------------
/**
 * Tipi di alloggio supportati per il blocco "Alloggio" della tappa.
 * Volutamente ridotti a tre opzioni esaustive per un viaggio in camper.
 */
export const LODGING_TYPES = ['CAMPEGGIO', 'AREA_CAMPER', 'LIBERA'];
export const LODGING_TYPE_LABELS = {
    CAMPEGGIO: { label: 'Campeggio', icon: '🏕️' },
    AREA_CAMPER: { label: 'Area camper', icon: '🅿️' },
    LIBERA: { label: 'Libera', icon: '🌲' },
};
/** Mapping WMO weather code → descrizione italiana + emoji */
export const WMO_CODES = {
    0: { it: 'Sereno', icon: '☀️' },
    1: { it: 'Prev. sereno', icon: '🌤️' },
    2: { it: 'Parz. nuvoloso', icon: '⛅' },
    3: { it: 'Nuvoloso', icon: '☁️' },
    45: { it: 'Nebbia', icon: '🌫️' },
    48: { it: 'Nebbia gelata', icon: '🌫️' },
    51: { it: 'Pioggerella leggera', icon: '🌦️' },
    53: { it: 'Pioggerella moderata', icon: '🌦️' },
    55: { it: 'Pioggerella intensa', icon: '🌦️' },
    56: { it: 'Pioggerella gelata', icon: '🌨️' },
    57: { it: 'Pioggerella gelata intensa', icon: '🌨️' },
    61: { it: 'Pioggia leggera', icon: '🌧️' },
    63: { it: 'Pioggia moderata', icon: '🌧️' },
    65: { it: 'Pioggia intensa', icon: '🌧️' },
    66: { it: 'Pioggia gelata', icon: '🌨️' },
    67: { it: 'Pioggia gelata intensa', icon: '🌨️' },
    71: { it: 'Neve leggera', icon: '🌨️' },
    73: { it: 'Neve moderata', icon: '🌨️' },
    75: { it: 'Neve intensa', icon: '🌨️' },
    77: { it: 'Granuli di neve', icon: '❄️' },
    80: { it: 'Rovesci leggeri', icon: '🌧️' },
    81: { it: 'Rovesci moderati', icon: '🌧️' },
    82: { it: 'Rovesci violenti', icon: '🌧️' },
    85: { it: 'Rovesci di neve leggeri', icon: '🌨️' },
    86: { it: 'Rovesci di neve intensi', icon: '🌨️' },
    95: { it: 'Temporale', icon: '⛈️' },
    96: { it: 'Temporale con grandine', icon: '⛈️' },
    99: { it: 'Temporale forte con grandine', icon: '⛈️' },
};
export function describeWeather(code) {
    return WMO_CODES[code] ?? { it: 'Condizione sconosciuta', icon: '🌡️' };
}
export const WEATHER_FORECAST_MAX_DAYS = 15;
/** Limiti hard upload — usati anche dal client per validazione prima del POST */
export const PHOTO_MAX_BYTES = 25 * 1024 * 1024; // 25 MB
export const PHOTO_ACCEPTED_MIME = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
];
// -----------------------------------------------------------------------------
// Cost (spese di viaggio)
// -----------------------------------------------------------------------------
export const COST_CATEGORIES = [
    'CARBURANTE',
    'CAMPEGGIO',
    'CIBO',
    'PEDAGGI',
    'ATTIVITA',
    'ALTRO',
];
export const COST_CATEGORY_LABELS = {
    CARBURANTE: { label: 'Carburante', icon: '⛽' },
    CAMPEGGIO: { label: 'Campeggio', icon: '🏕️' },
    CIBO: { label: 'Cibo', icon: '🍽️' },
    PEDAGGI: { label: 'Pedaggi', icon: '🛣️' },
    ATTIVITA: { label: 'Attività', icon: '🎟️' },
    ALTRO: { label: 'Altro', icon: '📦' },
};
// -----------------------------------------------------------------------------
// Impostazioni (account unico, condivise tra dispositivi)
// -----------------------------------------------------------------------------
export const NAV_APP_MOBILE = ['auto', 'waze', 'google', 'apple', 'system'];
export const NAV_APP_DESKTOP = ['google', 'waze', 'osm'];
// -----------------------------------------------------------------------------
// POI — punti di interesse (aree sosta, campeggi, soste libere, ecc.)
// -----------------------------------------------------------------------------
export const POI_TYPES = [
    'AREA_SOSTA',
    'CAMPEGGIO',
    'SOSTA_LIBERA',
    'PUNTO_ACQUA',
    'PUNTO_SCARICO',
    'ALTRO',
];
export const POI_TYPE_LABELS = {
    AREA_SOSTA: { label: 'Area sosta', icon: '🅿️' },
    CAMPEGGIO: { label: 'Campeggio', icon: '🏕️' },
    SOSTA_LIBERA: { label: 'Sosta libera', icon: '🌲' },
    PUNTO_ACQUA: { label: 'Punto acqua', icon: '💧' },
    PUNTO_SCARICO: { label: 'Punto scarico', icon: '🚿' },
    ALTRO: { label: 'Altro', icon: '📍' },
};
// -----------------------------------------------------------------------------
// Livella (ex Cunei Smart) — configurazione e autotaratura
// -----------------------------------------------------------------------------
export * from './leveling.js';
/** Come è appoggiato il telefono sul piano di riferimento. Senza questo dato il
 *  segno del beccheggio è un tiro a indovinare. */
export const PHONE_FACING = ['front', 'rear'];
/** Limiti fisici plausibili, condivisi fra client e server. Servono a intercettare
 *  l'errore di unità di misura (metri o millimetri al posto dei centimetri), che
 *  altrimenti darebbe consigli assurdi senza che niente segnali un problema. */
export const LEVELER_LIMITS = {
    track: { min: 50, max: 350 }, // carreggiata in cm
    wheelbase: { min: 150, max: 600 }, // passo in cm
    step: { min: 0.1, max: 100 }, // altezza di un gradino in cm
    maxSteps: 20,
    gain: { min: 0.3, max: 3.0 }, // fattori di autotaratura
    angle: { min: -45, max: 45 }, // gradi
};
/** Punto di partenza da correggere col metro, non un dato certo. */
export const LEVELER_DEFAULT_STEPS = [2.8, 5.5, 8.5, 11.5];
//# sourceMappingURL=index.js.map